"use client";

import { Card, Table, Pagination } from "react-bootstrap";
import { useState, useMemo, useEffect } from "react";

const ITEMS_PER_PAGE = 10;

const timeToSeconds = (timeString) => {
  if (!timeString) return 0;
  const [h = 0, m = 0, s = 0] = timeString.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

const calculateProductiveTime = (worked, brk, idle, meeting) => {
  const workedSec = timeToSeconds(worked);
  const breakSec = timeToSeconds(brk);
  const idleSec = timeToSeconds(idle);
  const meetingSec = timeToSeconds(meeting);
  const productiveSec = Math.max(0, workedSec - breakSec - idleSec - meetingSec);
  const hours = Math.floor(productiveSec / 3600);
  const minutes = Math.floor((productiveSec % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const calculateProductivityPercentage = (worked, brk, idle, meeting) => {
  const workedSec = timeToSeconds(worked);
  if (workedSec === 0) return 0;
  const productiveSec = Math.max(
    0,
    workedSec - timeToSeconds(brk) - timeToSeconds(idle) - timeToSeconds(meeting)
  );
  return Math.round((productiveSec / workedSec) * 100);
};

const AllProjects = ({ reports = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [reports]);

  const totalPages = Math.ceil(reports.length / ITEMS_PER_PAGE);

  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return reports.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [reports, currentPage]);

  return (
    <Card className="bg-white border-0 rounded-3 mb-4">
      <Card.Body className="p-0">
        <div className="p-4 d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Day-wise Activity Report</h3>
        </div>

        <div className="table-responsive p-4">
          <Table className="align-middle">
            <thead>
              <tr>
                <th>Date</th>
                <th>Worked Time</th>
                <th>Break Time</th>
                <th>Idle Time</th>
                <th>Meeting Time</th> {/* New column */}
                <th>Productive Time</th>
                <th>Productivity %</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReports.length > 0 ? (
                paginatedReports.map((r) => (
                  <tr key={r.date}>
                    <td>{new Date(r.date).toLocaleDateString()}</td>
                    <td>{r.workedHours}</td>
                    <td>{r.breakTime}</td>
                    <td>{r.idleTime}</td>
                    <td>{r.meetingTime}</td> {/* Show meeting time */}
                    <td>
                      {calculateProductiveTime(
                        r.workedHours,
                        r.breakTime,
                        r.idleTime,
                        r.meetingTime
                      )}
                    </td>
                    <td>
                      {calculateProductivityPercentage(
                        r.workedHours,
                        r.breakTime,
                        r.idleTime,
                        r.meetingTime
                      )}
                      %
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center">
                    No reports found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination className="justify-content-center mt-3">
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              />
              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={currentPage === index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              />
            </Pagination>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default AllProjects;